import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Button, Chip, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  ExpandMore, Science, Add, Delete, Save, CheckCircle, Analytics
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';

interface AnalysisResult {
  element: string;
  value: number;
  unit: string;
  method: string;
  uncertainty?: number;
  detection_limit?: number;
  notes?: string;
}

interface AnalysisFormData {
  sample_id: string;
  analyst_id: string;
  analysis_method: string;
  start_time?: Date;
  end_time?: Date;
  results: AnalysisResult[];
  qc_samples: string[];
  standards_used: string[];
  instruments_used: string[];
  analysis_conditions: {
    temperature?: number;
    pressure?: number;
    sample_weight_g?: number;
    dilution_factor?: number;
  };
  quality_control: {
    blank_contamination: boolean;
    duplicate_precision_ok: boolean;
    spike_recovery_ok: boolean;
    crm_recovery_percentage?: number;
  };
  notes: string;
  final_approved: boolean;
}

interface AnalysisFormProps {
  initialData?: Partial<AnalysisFormData>;
  onSubmit: (data: AnalysisFormData, action: 'SAVE_DRAFT' | 'SAVE_RESULTS' | 'FINALIZE_ANALYSIS') => void;
  onCancel: () => void;
  loading?: boolean;
  analysts: Array<{ id: string; name: string; speciality?: string }>;
}

const AnalysisForm: React.FC<AnalysisFormProps> = ({
  initialData, onSubmit, onCancel, loading = false, analysts
}) => {
  // Helper function to safely convert date values
  const safeDate = (dateValue: any): Date | undefined => {
    if (!dateValue) return undefined;
    const date = new Date(dateValue);
    return !isNaN(date.getTime()) ? date : undefined;
  };

  const [formData, setFormData] = useState<AnalysisFormData>({
    sample_id: '',
    analyst_id: '',
    analysis_method: 'FIRE_ASSAY',
    results: [],
    qc_samples: [],
    standards_used: [],
    instruments_used: [],
    analysis_conditions: {},
    quality_control: {
      blank_contamination: false,
      duplicate_precision_ok: false,
      spike_recovery_ok: false
    },
    notes: '',
    final_approved: false,
    ...initialData,
    // Override date fields with safe date conversion
    start_time: safeDate(initialData?.start_time),
    end_time: safeDate(initialData?.end_time),
  });

  const [newResult, setNewResult] = useState<AnalysisResult>({
    element: '', value: 0, unit: 'ppm', method: '', uncertainty: 0, detection_limit: 0
  });

  // Mining Elements
  const miningElements = [
    'Au', 'Ag', 'Cu', 'Pb', 'Zn', 'Fe', 'Al', 'Ca', 'Mg', 'K', 'Na', 'Si', 'S', 'P',
    'Mn', 'Cr', 'Ni', 'Co', 'As', 'Sb', 'Bi', 'Cd', 'Hg', 'Se', 'Te', 'Mo', 'W', 'Sn'
  ];

  // Analysis Methods
  const analysisMethods = [
    { value: 'FIRE_ASSAY', label: 'Fire Assay (Au, Ag, PGE)' },
    { value: 'ICP_MS', label: 'ICP-MS Multi-éléments' },
    { value: 'ICP_OES', label: 'ICP-OES Éléments Majeurs' },
    { value: 'XRF', label: 'Fluorescence X (XRF)' },
    { value: 'AAS', label: 'Absorption Atomique' },
    { value: 'GRAVIMETRIE', label: 'Analyse Gravimétrique' },
    { value: 'TITRAGE', label: 'Titrage Volumétrique' },
    { value: 'SPECTROPHOTOMETRIE', label: 'Spectrophotométrie UV-Vis' }
  ];

  // Units
  const units = ['ppm', 'ppb', '%', 'g/t', 'mg/kg', 'μg/g', 'oz/t'];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddResult = () => {
    if (newResult.element && newResult.method) {
      setFormData(prev => ({
        ...prev,
        results: [...prev.results, { ...newResult }]
      }));
      setNewResult({ element: '', value: 0, unit: 'ppm', method: '', uncertainty: 0, detection_limit: 0 });
    }
  };

  const handleDeleteResult = (index: number) => {
    setFormData(prev => ({
      ...prev,
      results: prev.results.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (action: 'SAVE_DRAFT' | 'SAVE_RESULTS' | 'FINALIZE_ANALYSIS'): boolean => {
    // For draft, minimal validation
    if (action === 'SAVE_DRAFT') {
      return true;
    }
    
    // For saving results, need basic info
    if (action === 'SAVE_RESULTS') {
      return formData.analyst_id !== '' && formData.analysis_method !== '';
    }
    
    // For finalization, need complete data
    if (action === 'FINALIZE_ANALYSIS') {
      return formData.analyst_id !== '' && 
             formData.analysis_method !== '' && 
             formData.results.length > 0 &&
             formData.final_approved;
    }
    
    return false;
  };

  const handleSubmit = (action: 'SAVE_DRAFT' | 'SAVE_RESULTS' | 'FINALIZE_ANALYSIS') => {
    if (validateForm(action)) {
      onSubmit(formData, action);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Formulaire d'Analyse Chimique
        </Typography>

        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Analytics sx={{ mr: 2 }} />
            <Typography variant="h6">Informations de Base</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Échantillon"
                  value={formData.sample_id}
                  onChange={(e) => handleInputChange('sample_id', e.target.value)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Analyste Responsable</InputLabel>
                  <Select
                    value={formData.analyst_id}
                    onChange={(e) => handleInputChange('analyst_id', e.target.value)}
                    label="Analyste Responsable"
                  >
                    {analysts.map((analyst) => (
                      <MenuItem key={analyst.id} value={analyst.id}>
                        {analyst.name} {analyst.speciality && `(${analyst.speciality})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Méthode d'Analyse</InputLabel>
                  <Select
                    value={formData.analysis_method}
                    onChange={(e) => handleInputChange('analysis_method', e.target.value)}
                    label="Méthode d'Analyse"
                  >
                    {analysisMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Heure de Début"
                  value={(() => {
                    // Triple-check date validity
                    if (!formData.start_time) return null;
                    try {
                      const date = new Date(formData.start_time);
                      return !isNaN(date.getTime()) ? date : null;
                    } catch {
                      return null;
                    }
                  })()}
                  onChange={(date) => {
                    // Ensure we only set valid dates
                    try {
                      if (date && !isNaN(new Date(date).getTime())) {
                        handleInputChange('start_time', date);
                      } else {
                        handleInputChange('start_time', undefined);
                      }
                    } catch {
                      handleInputChange('start_time', undefined);
                    }
                  }}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true,
                      error: false, // Prevent error state from invalid dates
                      helperText: "Optionnel - Heure de début d'analyse"
                    } 
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Analysis Conditions */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Science sx={{ mr: 2 }} />
            <Typography variant="h6">Conditions d'Analyse</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Poids Échantillon (g)"
                  type="number"
                  value={formData.analysis_conditions.sample_weight_g || ''}
                  onChange={(e) => handleInputChange('analysis_conditions', {
                    ...formData.analysis_conditions,
                    sample_weight_g: parseFloat(e.target.value) || undefined
                  })}
                  inputProps={{ step: 0.001 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Température (°C)"
                  type="number"
                  value={formData.analysis_conditions.temperature || ''}
                  onChange={(e) => handleInputChange('analysis_conditions', {
                    ...formData.analysis_conditions,
                    temperature: parseFloat(e.target.value) || undefined
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Pression (bar)"
                  type="number"
                  value={formData.analysis_conditions.pressure || ''}
                  onChange={(e) => handleInputChange('analysis_conditions', {
                    ...formData.analysis_conditions,
                    pressure: parseFloat(e.target.value) || undefined
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Facteur de Dilution"
                  type="number"
                  value={formData.analysis_conditions.dilution_factor || ''}
                  onChange={(e) => handleInputChange('analysis_conditions', {
                    ...formData.analysis_conditions,
                    dilution_factor: parseFloat(e.target.value) || undefined
                  })}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Results Entry */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Save sx={{ mr: 2 }} />
            <Typography variant="h6">Saisie des Résultats</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Add New Result */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Ajouter un Résultat</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Élément</InputLabel>
                      <Select
                        value={newResult.element}
                        onChange={(e) => setNewResult(prev => ({ ...prev, element: e.target.value }))}
                        label="Élément"
                      >
                        {miningElements.map((element) => (
                          <MenuItem key={element} value={element}>{element}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Valeur"
                      type="number"
                      value={newResult.value}
                      onChange={(e) => setNewResult(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unité</InputLabel>
                      <Select
                        value={newResult.unit}
                        onChange={(e) => setNewResult(prev => ({ ...prev, unit: e.target.value }))}
                        label="Unité"
                      >
                        {units.map((unit) => (
                          <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Méthode"
                      value={newResult.method}
                      onChange={(e) => setNewResult(prev => ({ ...prev, method: e.target.value }))}
                      placeholder="Ex: FA-AAS"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Incertitude"
                      type="number"
                      value={newResult.uncertainty || ''}
                      onChange={(e) => setNewResult(prev => ({ ...prev, uncertainty: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Limite Détection"
                      type="number"
                      value={newResult.detection_limit || ''}
                      onChange={(e) => setNewResult(prev => ({ ...prev, detection_limit: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button
                      variant="contained"
                      onClick={handleAddResult}
                      startIcon={<Add />}
                      disabled={!newResult.element || !newResult.method}
                    >
                      Ajouter
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Results Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Élément</TableCell>
                    <TableCell align="right">Valeur</TableCell>
                    <TableCell>Unité</TableCell>
                    <TableCell>Méthode</TableCell>
                    <TableCell align="right">Incertitude</TableCell>
                    <TableCell align="right">Limite Détection</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.results.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell><strong>{result.element}</strong></TableCell>
                      <TableCell align="right">{result.value}</TableCell>
                      <TableCell>{result.unit}</TableCell>
                      <TableCell>{result.method}</TableCell>
                      <TableCell align="right">{result.uncertainty || '-'}</TableCell>
                      <TableCell align="right">{result.detection_limit || '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteResult(index)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {formData.results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary">Aucun résultat ajouté</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Quality Control */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <CheckCircle sx={{ mr: 2 }} />
            <Typography variant="h6">Contrôle Qualité</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.quality_control.blank_contamination}
                      onChange={(e) => handleInputChange('quality_control', {
                        ...formData.quality_control,
                        blank_contamination: e.target.checked
                      })}
                    />
                  }
                  label="Blanc de contamination validé"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.quality_control.duplicate_precision_ok}
                      onChange={(e) => handleInputChange('quality_control', {
                        ...formData.quality_control,
                        duplicate_precision_ok: e.target.checked
                      })}
                    />
                  }
                  label="Précision des duplicatas OK"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.quality_control.spike_recovery_ok}
                      onChange={(e) => handleInputChange('quality_control', {
                        ...formData.quality_control,
                        spike_recovery_ok: e.target.checked
                      })}
                    />
                  }
                  label="Récupération des ajouts dosés OK"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Récupération CRM (%)"
                  type="number"
                  value={formData.quality_control.crm_recovery_percentage || ''}
                  onChange={(e) => handleInputChange('quality_control', {
                    ...formData.quality_control,
                    crm_recovery_percentage: parseFloat(e.target.value) || undefined
                  })}
                  inputProps={{ min: 0, max: 200 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Final Notes */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Notes et Finalisation</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes d'Analyse"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Observations, conditions particulières, problèmes rencontrés..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.final_approved}
                      onChange={(e) => handleInputChange('final_approved', e.target.checked)}
                    />
                  }
                  label="Analyse finalisée et approuvée"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Annuler
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => handleSubmit('SAVE_DRAFT')}
              disabled={loading}
              startIcon={<Save />}
              color="info"
            >
              Sauvegarder Brouillon
            </Button>
            
            <Button
              variant="contained"
              onClick={() => handleSubmit('SAVE_RESULTS')}
              disabled={loading || !formData.analyst_id || !formData.analysis_method}
              startIcon={<Save />}
              color="primary"
            >
              Sauvegarder Résultats
            </Button>
            
            <Button
              variant="contained"
              onClick={() => handleSubmit('FINALIZE_ANALYSIS')}
              disabled={loading || formData.results.length === 0 || !formData.final_approved}
              startIcon={<CheckCircle />}
              color="success"
            >
              Finaliser Analyse
            </Button>
          </Box>
        </Box>

        {/* Summary */}
        {formData.results.length > 0 && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Résumé de l'Analyse</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Méthode: <strong>{analysisMethods.find(m => m.value === formData.analysis_method)?.label}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nombre d'éléments: <strong>{formData.results.length}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Éléments analysés:</Typography>
                  <Box sx={{ mt: 1 }}>
                    {formData.results.map((result, index) => (
                      <Chip
                        key={index}
                        label={`${result.element}: ${result.value} ${result.unit}`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AnalysisForm; 